import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarDays,
  Users,
  Mail,
  Phone,
  CreditCard,
  FileText,
  MapPin,
  Loader2,
  Bed,
  DollarSign,
  Pencil,
  Check,
  X,
  Undo2,
} from "lucide-react";
import {
  LogIn,
  LogOut,
  Receipt,
  ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Textarea } from "@/components/ui/textarea";
import { computeReservationGrandTotal, computeReservationBalance } from "@/lib/reservation-total";
import { PaymentMethodLabel } from "@/components/shared/PaymentMethodLabel";


type ReservationDetail = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  document_id: string | null;
  city: string | null;
  check_in: string;
  check_out: string;
  guests: number;
  subtotal: number;
  service_fee: number;
  total: number;
  deposit_amount: number | null;
  deposit_paid: boolean | null;
  balance_amount: number | null;
  status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  special_requests: string | null;
  created_at: string;
  accommodation_id: string | null;
  checked_in_at: string | null;
  checked_in_by: string | null;
  checked_out_at: string | null;
  checked_out_by: string | null;
  balance_payment_method: string | null;
  origin: string | null;
  payment_id: string | null;
};

type InvoiceRow = {
  id: string;
  invoice_number: string;
  total: number;
  status: string;
  cufe: string | null;
  qr_code_url: string | null;
  created_at: string;
  invoice_type: string;
  factus_connector: { name: string } | null;
};

type IncomeRow = {
  id: string;
  income_type: string;
  amount: number;
  tax_amount: number;
  source_label: string | null;
  notes: string | null;
  paid_at: string;
  receipt_number: string | null;
  payment_method: { name: string; owner_name: string | null } | null;
};

type Accommodation = {
  id: string;
  name: string;
};

interface ReservationDetailModalProps {
  reservationId: string | null;
  onClose: () => void;
}

const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  completed: "Completada",
  no_show: "No show",
};

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  no_show: "bg-gray-100 text-gray-800 border-gray-200",
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

type AdditionalServiceRow = {
  quantity: number;
  total_price: number;
  decoration_type: string | null;
  decoration_color: string | null;
  additional_service: { name: string; service_type: string } | null;
};

type RefundRow = {
  id: string;
  amount: number;
  category: string;
  description: string | null;
  paid_at: string;
  receipt_number: string | null;
  payment_method_name?: string;
};

type PaymentMethodOption = {
  id: string;
  name: string;
  owner_name: string | null;
};

export function ReservationDetailModal({ reservationId, onClose }: ReservationDetailModalProps) {
  const [reservation, setReservation] = useState<ReservationDetail | null>(null);
  const [accommodation, setAccommodation] = useState<Accommodation | null>(null);
  const [services, setServices] = useState<AdditionalServiceRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [incomeRows, setIncomeRows] = useState<IncomeRow[]>([]);
  const [currentMpAccount, setCurrentMpAccount] = useState<string | null>(null);
  const [hasDepositIncome, setHasDepositIncome] = useState(true);
  const [registeringDeposit, setRegisteringDeposit] = useState(false);
  const [editingRequests, setEditingRequests] = useState(false);
  const [editedRequests, setEditedRequests] = useState("");
  const [savingRequests, setSavingRequests] = useState(false);
  const [refunds, setRefunds] = useState<RefundRow[]>([]);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethodId, setRefundMethodId] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [savingRefund, setSavingRefund] = useState(false);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodOption[]>([]);
  const { hasRole } = useAuth();
  const canEditRequests = hasRole("admin") || hasRole("gerencia") || hasRole("recepcion") || hasRole("rrss");
  const canRegisterRefund = hasRole("admin") || hasRole("gerencia");

  const loadRefunds = useCallback(async (currentReservationId: string) => {
    const { data: refundData, error: refundError } = await (supabase
      .from("expense_transactions")
      .select("id, amount, category, description, paid_at, receipt_number, payment_method_id") as any)
      .eq("reservation_id", currentReservationId)
      .order("paid_at", { ascending: true });

    if (refundError) {
      console.error("Error cargando devoluciones", refundError);
      setRefunds([]);
      return;
    }

    if (!refundData || refundData.length === 0) {
      setRefunds([]);
      return;
    }

    const pmIds = [...new Set((refundData as any[]).map((r: any) => r.payment_method_id).filter(Boolean))] as string[];
    const { data: pmData } = pmIds.length
      ? await supabase.from("payment_methods").select("id, name, owner_name").in("id", pmIds)
      : { data: [] };
    const pmMap = new Map((pmData || []).map((p: any) => [p.id, p.owner_name ? `${p.name} (${p.owner_name})` : p.name]));

    setRefunds((refundData as any[]).map((r: any) => ({
      id: r.id,
      amount: Number(r.amount) || 0,
      category: r.category,
      description: r.description,
      paid_at: r.paid_at,
      receipt_number: r.receipt_number,
      payment_method_name: pmMap.get(r.payment_method_id) || "Otro",
    })));
  }, []);

  useEffect(() => {
    if (!reservationId) {
      setReservation(null);
      setAccommodation(null);
      setServices([]);
      setHasDepositIncome(true);
      setRefunds([]);
      return;
    }

    const fetchReservation = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("reservations")
        .select("*")
        .eq("id", reservationId)
        .single();

      if (!error && data) {
        setReservation(data);

        if (data.accommodation_id) {
          const { data: accData } = await supabase
            .from("accommodations_public")
            .select("id, name")
            .eq("id", data.accommodation_id)
            .single();
          if (accData) setAccommodation(accData);

          const { data: accMp } = await supabase
            .from("accommodations")
            .select("mercadopago_account_id")
            .eq("id", data.accommodation_id)
            .maybeSingle();
          if (accMp?.mercadopago_account_id) {
            const { data: mpAcc } = await supabase
              .from("mercadopago_accounts")
              .select("name")
              .eq("id", accMp.mercadopago_account_id)
              .maybeSingle();
            setCurrentMpAccount(mpAcc?.name || null);
          } else {
            setCurrentMpAccount(null);
          }
        }

        const { data: svcData } = await supabase
          .from("reservation_additional_services")
          .select("quantity, total_price, decoration_type, decoration_color, additional_service:additional_services(name, service_type)")
          .eq("reservation_id", reservationId);
        if (svcData) setServices(svcData as unknown as AdditionalServiceRow[]);

        const { data: invData } = await supabase
          .from("invoices")
          .select("id, invoice_number, total, status, cufe, qr_code_url, created_at, invoice_type, factus_connector:factus_connectors(name)")
          .eq("reservation_id", reservationId)
          .order("created_at", { ascending: true });
        setInvoices((invData || []) as unknown as InvoiceRow[]);

        const { data: incData } = await supabase
          .from("income_transactions")
          .select("id, income_type, amount, tax_amount, source_label, notes, paid_at, receipt_number, payment_method:payment_methods(name, owner_name)")
          .eq("reservation_id", reservationId)
          .order("paid_at", { ascending: true });
        setIncomeRows((incData || []) as unknown as IncomeRow[]);

        await loadRefunds(reservationId);

        if (data.status === "no_show" && data.deposit_paid && (data.deposit_amount || 0) > 0) {
          const { data: incomeTx } = await supabase
            .from("income_transactions")
            .select("id")
            .eq("reservation_id", reservationId)
            .eq("income_type", "hotel_deposit")
            .maybeSingle();
          setHasDepositIncome(!!incomeTx);
        } else {
          setHasDepositIncome(true);
        }
      }
      setIsLoading(false);
    };

    const fetchPaymentMethods = async () => {
      const { data } = await supabase
        .from("payment_methods")
        .select("id, name, owner_name")
        .eq("is_active", true)
        .contains("applies_to", ["hotel"])
        .order("display_order", { ascending: true });
      setPaymentMethodOptions((data || []) as PaymentMethodOption[]);
    };

    fetchReservation();
    fetchPaymentMethods();
  }, [loadRefunds, reservationId]);

  const handleRegisterDeposit = async () => {
    if (!reservation) return;
    setRegisteringDeposit(true);
    const { data: mpMethod } = await supabase
      .from("payment_methods")
      .select("id")
      .eq("code", "mercadopago")
      .maybeSingle();
    if (mpMethod) {
      const { error } = await supabase.from("income_transactions").insert({
        income_type: "hotel_deposit",
        reservation_id: reservation.id,
        customer_name: `${reservation.first_name} ${reservation.last_name}`,
        source_label: `✓ Anticipo No-Show - ${accommodation?.name || "Alojamiento"}`,
        amount: reservation.deposit_amount!,
        payment_method_id: mpMethod.id,
        paid_at: new Date().toISOString(),
      });
      if (!error) {
        toast.success(`Anticipo de ${formatCurrency(reservation.deposit_amount!)} registrado en contabilidad`);
        setHasDepositIncome(true);
      } else {
        toast.error("Error al registrar anticipo");
      }
    }
    setRegisteringDeposit(false);
  };

  const handleRegisterRefund = async () => {
    if (!reservation || !refundAmount || !refundMethodId || !refundReason) {
      toast.error("Completa todos los campos de la devolución");
      return;
    }
    setSavingRefund(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("expense_transactions").insert({
      category: "Reembolsos",
      description: refundReason,
      amount: parseFloat(refundAmount),
      payment_method_id: refundMethodId,
      department: "hotel",
      reservation_id: reservation.id,
      created_by: user?.id,
      paid_at: new Date().toISOString(),
    } as any);
    if (!error) {
      toast.success(`Devolución de ${formatCurrency(parseFloat(refundAmount))} registrada`);
      setShowRefundForm(false);
      setRefundAmount("");
      setRefundMethodId("");
      setRefundReason("");
      await loadRefunds(reservation.id);
    } else {
      toast.error("Error al registrar devolución");
    }
    setSavingRefund(false);
  };

  return (
    <Dialog open={!!reservationId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Detalle de Reserva
            {reservation?.status && (
              <Badge variant="outline" className={statusColors[reservation.status] || ""}>
                {statusLabels[reservation.status] || reservation.status}
              </Badge>
            )}
            {reservation?.origin === "airbnb" && (
              <Badge variant="outline" className="bg-[#FF5A5F] text-white border-[#FF5A5F]">
                Airbnb
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : reservation ? (
          <div className="space-y-4">
            {/* Guest Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Huésped
              </h4>
              <p className="font-medium text-lg">
                {reservation.first_name} {reservation.last_name}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="truncate">{reservation.email}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>{reservation.phone}</span>
                </div>
                {reservation.document_id && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="h-4 w-4 shrink-0" />
                    <span>{reservation.document_id}</span>
                  </div>
                )}
                {reservation.city && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span>{reservation.city}</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Stay Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Estadía
              </h4>
              {accommodation && (
                <div className="flex items-center gap-2 text-sm">
                  <Bed className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{accommodation.name}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-4 w-4 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Check-in</p>
                    <p className="font-medium text-foreground">
                      {format(new Date(reservation.check_in + "T12:00:00"), "d MMM yyyy", { locale: es })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-4 w-4 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Check-out</p>
                    <p className="font-medium text-foreground">
                      {format(new Date(reservation.check_out + "T12:00:00"), "d MMM yyyy", { locale: es })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{reservation.guests} huéspedes</span>
              </div>
            </div>

            <Separator />

            {/* Payment Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Pago
              </h4>
              {services.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-1 text-sm mb-2">
                  <p className="font-semibold text-xs uppercase tracking-wide text-primary">Servicios Adicionales</p>
                  {services.map((s, i) => {
                    const name = s.additional_service?.name || "Servicio";
                    const typeLabel = s.decoration_type === "birthday" ? "🎂 Cumpleaños" : s.decoration_type === "romantic" ? "❤️ Romántica" : "";
                    const colorLabel = s.decoration_color === "female" ? "🎀 Estilo Mujer" : s.decoration_color === "male" ? "🎩 Estilo Hombre" : "";
                    const hasDecorationDetails = typeLabel || colorLabel;
                    return (
                      <div key={i} className="space-y-0.5">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {name} × {s.quantity}
                          </span>
                          <span>{formatCurrency(s.total_price)}</span>
                        </div>
                        {hasDecorationDetails && (
                          <div className="flex flex-wrap gap-2 ml-2">
                            {typeLabel && (
                              <span className="text-xs bg-background border rounded px-1.5 py-0.5">{typeLabel}</span>
                            )}
                            {colorLabel && (
                              <span className="text-xs bg-background border rounded px-1.5 py-0.5">{colorLabel}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
              {(() => {
                  const servicesTotalAmount = services.reduce((sum, s) => sum + (s.total_price || 0), 0);
                  const res = reservation as typeof reservation & {
                    service_fee?: number;
                    total_incluye_servicios?: boolean | null;
                    checked_in_at?: string | null;
                  };
                  const { grandTotal } = computeReservationGrandTotal({
                    total: reservation.total || 0,
                    subtotal: reservation.subtotal,
                    serviceFee: res.service_fee ?? 0,
                    servicesTotal: servicesTotalAmount,
                    totalIncluyeServicios: res.total_incluye_servicios ?? null,
                  });
                  const tariffPayments = incomeRows
                    .filter((i) => i.income_type === "hotel_deposit" || i.income_type === "hotel_balance")
                    .reduce((sum, i) => sum + (i.amount || 0), 0);
                  const postCheckIn =
                    !!res.checked_in_at || reservation.status === "completed" || reservation.status === "no_show";
                  const balanceInfo = computeReservationBalance({
                    grandTotal,
                    depositAmount: reservation.deposit_amount || 0,
                    tariffPayments,
                    postCheckIn,
                    checkOutPast: new Date(reservation.check_out + "T12:00:00") < new Date(),
                    depositPaid: (reservation as { deposit_paid?: boolean | null }).deposit_paid ?? null,
                  });

                  const checkIn = new Date(reservation.check_in + "T12:00:00");
                  const checkOut = new Date(reservation.check_out + "T12:00:00");
                  const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{nights} {nights === 1 ? "noche" : "noches"}</span>
                        <span>{formatCurrency(reservation.subtotal)}</span>
                      </div>
                      {servicesTotalAmount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Servicios adicionales</span>
                          <span>{formatCurrency(servicesTotalAmount)}</span>
                        </div>
                      )}
                      <Separator className="my-1" />
                      <div className="flex justify-between font-semibold">
                        <span>Total estadía</span>
                        <span>{formatCurrency(grandTotal)}</span>
                      </div>
                      {(reservation.deposit_amount ?? 0) > 0 && (
                        <>
                          <div className="flex justify-between text-xs pt-1">
                            <span className="text-muted-foreground">
                              Anticipo {reservation.deposit_paid ? "(pagado)" : "(pendiente)"}
                            </span>
                            <span>{formatCurrency(reservation.deposit_amount || 0)}</span>
                          </div>
                          {balanceInfo.paidAmount !== (reservation.deposit_amount || 0) && (
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Pagos de tarifa registrados</span>
                              <span>{formatCurrency(balanceInfo.paidAmount)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">{balanceInfo.label}</span>
                            <span
                              className={
                                balanceInfo.status === "revisar"
                                  ? "text-amber-600 font-medium"
                                  : balanceInfo.status === "no_cobrado_al_cierre"
                                    ? "text-orange-600 font-medium"
                                    : ""
                              }
                            >
                              {formatCurrency(Math.abs(balanceInfo.balance))}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  );

                })()}
              </div>
              {reservation.payment_method && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CreditCard className="h-4 w-4" />
                  <span>Método: {reservation.payment_method}</span>
                </div>
              )}
            </div>

            {(reservation.special_requests || canEditRequests) && (
              <>
                <Separator />
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      Solicitudes especiales
                    </h4>
                    {canEditRequests && !editingRequests && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 shrink-0 gap-1.5 px-2"
                        onClick={() => {
                          setEditedRequests(reservation.special_requests || "");
                          setEditingRequests(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </Button>
                    )}
                  </div>
                  {editingRequests ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editedRequests}
                        onChange={(e) => setEditedRequests(e.target.value)}
                        rows={3}
                        className="text-sm"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingRequests(false)}
                          disabled={savingRequests}
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Cancelar
                        </Button>
                        <Button
                          size="sm"
                          disabled={savingRequests}
                          onClick={async () => {
                            setSavingRequests(true);
                            const { error } = await supabase
                              .from("reservations")
                              .update({ special_requests: editedRequests || null })
                              .eq("id", reservation.id);
                            if (!error) {
                              setReservation({ ...reservation, special_requests: editedRequests || null });
                              toast.success("Solicitudes especiales actualizadas");
                              setEditingRequests(false);
                            } else {
                              toast.error("Error al guardar");
                            }
                            setSavingRequests(false);
                          }}
                        >
                          {savingRequests ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm">{reservation.special_requests || <span className="text-muted-foreground italic">Sin solicitudes</span>}</p>
                  )}
                </div>
              </>
            )}
            {reservation.status === "no_show" && !hasDepositIncome && reservation.deposit_paid && (reservation.deposit_amount || 0) > 0 && (
              <>
                <Separator />
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    💰 Anticipo de {formatCurrency(reservation.deposit_amount!)} pendiente de registro en contabilidad
                  </p>
                  <Button
                    size="sm"
                    onClick={handleRegisterDeposit}
                    disabled={registeringDeposit}
                    className="w-full"
                  >
                    {registeringDeposit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
                    Registrar anticipo en contabilidad
                  </Button>
                </div>
              </>
            )}

            <div className="text-xs text-muted-foreground pt-2">
              Creada el {format(new Date(reservation.created_at), "d MMM yyyy, HH:mm", { locale: es })}
            </div>

            {/* Check-in / Check-out Status */}
            {reservation.checked_in_at && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Estado Operativo
                  </h4>
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      <LogIn className="h-4 w-4" />
                      Check-in realizado
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(reservation.checked_in_at), "d MMM yyyy, HH:mm", { locale: es })}
                    </p>
                    {reservation.balance_payment_method && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CreditCard className="h-3.5 w-3.5" />
                        Saldo pagado con: <PaymentMethodLabel value={reservation.balance_payment_method} />
                      </div>
                    )}
                  </div>
                  {reservation.checked_out_at && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                        <LogOut className="h-4 w-4" />
                        Check-out realizado
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(reservation.checked_out_at), "d MMM yyyy, HH:mm", { locale: es })}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Invoices */}
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Facturas Electrónicas
              </h4>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="bg-muted/50 rounded-lg p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-medium text-sm truncate">{inv.invoice_number}</span>
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {inv.status === "validated" ? "Validada" : inv.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {format(new Date(inv.created_at), "d MMM yyyy, HH:mm", { locale: es })} · {formatCurrency(inv.total)}
                          {inv.factus_connector?.name && ` · Facturó: ${inv.factus_connector.name}`}
                        </p>
                      </div>
                      {inv.qr_code_url && (
                        <a href={inv.qr_code_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  Sin factura electrónica generada para esta reserva.
                </div>
              )}
            </div>

            {/* Income Transactions */}
            <Separator />
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Pagos Registrados
              </h4>
              {incomeRows.length > 0 ? (
                <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm">
                  {incomeRows.map((inc) => {
                    const typeLabels: Record<string, string> = {
                      hotel_deposit: "Anticipo",
                      hotel_balance: "Saldo",
                      hotel_extra: "Extras",
                    };
                    return (
                      <div key={inc.id} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="font-medium truncate">
                              {typeLabels[inc.income_type] || inc.income_type}
                            </span>
                            {inc.payment_method?.name && (
                              <span className="text-xs text-muted-foreground">
                                · {inc.payment_method.name}
                                {inc.payment_method.owner_name && ` (${inc.payment_method.owner_name})`}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground pl-5">
                            {format(new Date(inc.paid_at), "d MMM yyyy, HH:mm", { locale: es })}
                            {inc.receipt_number && ` · ${inc.receipt_number}`}
                            {inc.tax_amount > 0 && ` · IVA: ${formatCurrency(inc.tax_amount)}`}
                          </p>
                        </div>
                        <span className="font-semibold shrink-0">{formatCurrency(inc.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : reservation.payment_id ? (
                <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-1">
                  <p>
                    <span className="font-medium">Pago online Mercado Pago</span>
                    {` · ID: ${reservation.payment_id}`}
                  </p>
                  <p className="text-muted-foreground">
                    Cuenta MP asignada actualmente a {accommodation?.name || "este alojamiento"}:{" "}
                    <span className="font-medium text-foreground">{currentMpAccount || "No configurada"}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground italic">
                    No hay registro histórico de ingreso. La cuenta mostrada es la asignación actual y puede no coincidir con la que recibió el pago original.
                  </p>
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                  Sin pagos registrados.
                </div>
              )}
            </div>


            {/* Refunds (linked expense_transactions) */}
            {refunds.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-destructive uppercase tracking-wide">
                    Devoluciones
                  </h4>
                  <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 space-y-2 text-sm">
                    {refunds.map((ref) => (
                      <div key={ref.id} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Undo2 className="h-3.5 w-3.5 text-destructive shrink-0" />
                            <span className="font-medium truncate text-destructive">
                              {ref.description || ref.category}
                            </span>
                            {ref.payment_method_name && (
                              <span className="text-xs text-muted-foreground">· {ref.payment_method_name}</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground pl-5">
                            {format(new Date(ref.paid_at), "d MMM yyyy, HH:mm", { locale: es })}
                            {ref.receipt_number && ` · ${ref.receipt_number}`}
                          </p>
                        </div>
                        <span className="font-semibold shrink-0 text-destructive">-{formatCurrency(ref.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Refund registration button & form */}
            {canRegisterRefund && reservation.status !== "cancelled" && (
              <>
                <Separator />
                {!showRefundForm ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 border-destructive/30 text-destructive hover:bg-destructive/5"
                    onClick={() => setShowRefundForm(true)}
                  >
                    <Undo2 className="h-4 w-4" />
                    Registrar devolución
                  </Button>
                ) : (
                  <div className="border border-destructive/20 rounded-lg p-4 space-y-3 bg-destructive/5">
                    <h4 className="text-sm font-semibold text-destructive">Nueva Devolución</h4>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Monto</Label>
                        <Input
                          type="number"
                          placeholder="600000"
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          min={0}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Método de pago</Label>
                        <Select value={refundMethodId} onValueChange={setRefundMethodId}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentMethodOptions.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.owner_name ? `${m.name} (${m.owner_name})` : m.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Motivo</Label>
                        <Textarea
                          placeholder="Ej: Checkout anticipado, 1 noche no utilizada"
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                          rows={2}
                          className="mt-1 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => { setShowRefundForm(false); setRefundAmount(""); setRefundMethodId(""); setRefundReason(""); }}>
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={savingRefund || !refundAmount || !refundMethodId || !refundReason}
                        onClick={handleRegisterRefund}
                      >
                        {savingRefund ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Undo2 className="h-4 w-4 mr-1" />}
                        Registrar
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No se encontró la reserva
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
